import axios from 'axios'
import cookies from 'js-cookie'
import * as rax from 'retry-axios'
import { setupCache } from 'axios-cache-interceptor'
import store from 'store'
import qs from 'qs'

const baseDomain = location.hostname.split('.').slice(-2).join('.')
const apiDomain = import.meta.env.VITE_API_DOMAIN ?? `newsdesk-api.${baseDomain}`
const apiDomainProtocol = import.meta.env.VITE_API_DOMAIN_PROTOCOL ?? `https`
const loginUrl = import.meta.env.VITE_LOGIN_URL ?? `https://${apiDomain}/okta/login`

const api = setupCache(
  axios.create({
    baseURL: `${apiDomainProtocol}://${apiDomain}`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=utf-8'
    },
    timeout: 50000,
    paramsSerializer: (params) => {
      return qs.stringify(params, {
        arrayFormat: 'brackets',
        filter: (prefix, value) => (value === null || typeof value === 'undefined' ? undefined : value) // remove null and undefined values
      })
    }
  })
)

// Add a request interceptor to add cache params
api.interceptors.request.use((config) => {
  // old axios-cache-adapter params:
  // cache: {
  //   maxAge: 5 * 1000, // 5 second default
  //   // debug: true,
  //   readHeaders: false,
  //   exclude: {
  //     query: false
  //   },
  //   ignoreCache: false,
  // }
  // default params
  let defaultCache = {
    ttl: 5 * 1000, // 5 second default --- Equivalent to `maxAge: false`
    interpretHeader: false, // --- Equivalent to `readHeaders: false`
    cachePredicate: {
      ignoreUrls: [] // allow caching of query parameter requests --- Equivalent to `exclude: { query: false }`
    },
    override: false // when this option is 'true', it bypasses the current cache and always make a new http request --- Equivalent to `ignoreCache: false`
  }
  if (typeof config?.cache?.maxAge !== 'undefined') {
    defaultCache.ttl = config.cache.maxAge
  }
  if (typeof config?.cache?.exclude?.query !== 'undefined') {
    if (config.cache.exclude.query) {
      defaultCache.cachePredicate.ignoreUrls = [/\?.+/] // // Ignore URLs that contain "?" (i.e., have query parameters)
    }
  }
  if (typeof config?.cache?.ignoreCache !== 'undefined') {
    defaultCache.override = config.cache.ignoreCache // This option bypasses the current cache and always make a new http request. This will not delete the current cache, it will just replace the cache when the response arrives.
    // or
    // defaultCache = false // this option will disable the cache for this request. This does not mean that the cache will be excluded from the storage.
  }

  config.cache = defaultCache
  return config
})

// Default axios retry configuration.  Currently retry is set to 0 so
// retries can be scoped to individual calls.  The other defaults
// still apply unless overridden in specific calls.
api.defaults.raxConfig = {
  instance: api,

  // Retry times on requests that return a response (500, etc) before giving up.
  retry: 0,

  // Retry twice on errors that don't return a response (ENOTFOUND, ETIMEDOUT, etc).
  noResponseRetries: 2,

  // Milliseconds to delay at first.  Defaults to 100. Only considered when backoffType is 'static'
  retryDelay: 500,

  backoffType: 'static', // 'exponential' (default), 'static' or 'linear'

  // Response status codes to retry.  Supports a double array with a list of ranges.
  statusCodesToRetry: [
    [400, 429],
    [500, 599]
  ],

  // HTTP methods to automatically retry.
  httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'DELETE', 'PUT', 'PATCH', 'POST'],

  onRetryAttempt: (err) => {
    const cfg = rax.getConfig(err)
    console.debug(`Retry attempt #${cfg.currentRetryAttempt}`)
  }
}

rax.attach(api)

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

// Add a request interceptor to add the Authorization header and CSRF token
api.interceptors.request.use((config) => {
  const accessToken = cookies.get('EWS_AUTH_KEY')
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Add CSRF token for state-changing requests
  // Laravel sets XSRF-TOKEN cookie, we need to send it in X-CSRF-TOKEN header
  const csrfToken = cookies.get('XSRF-TOKEN')
  if (csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())) {
    config.headers['X-CSRF-TOKEN'] = csrfToken
  }

  return config
})

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    if (401 === error?.response?.status && error?.config?.baseURL?.includes('api.scrippsnet')) {
      // if we got a 401 on the refresh token endpoint, go to login
      if (originalRequest.url === '/v2/auth/refresh_token') {
        console.log('401 error on retry', error)
        store.remove('ews_user')
        store.remove('ews_current_station')
        store.remove('ews_station_settings')
        store.remove('ews_container')
        cookies.remove('EWS_AUTH_KEY', { domain: `.${baseDomain}` })

        const prev = encodeURIComponent(location.href)
        window.location.href = `${loginUrl}?prev=${prev}&supports_okta_jwt=true`
        return Promise.reject(error)
      }
      console.log('401 error', error)
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Call refresh token endpoint, signalling Okta JWT support so the backend
        // returns an Okta-signed access token instead of the legacy self-signed JWT.
        console.log('refreshing token')
        const response = await api.get('/v2/auth/refresh_token', {
          headers: { 'X-Supports-Okta-JWT': 'true' },
          cache: false
        })
        const { jwt } = response.data

        // Update tokens in cookie
        cookies.set('EWS_AUTH_KEY', jwt, { domain: `.${baseDomain}` })

        processQueue(null, jwt)

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${jwt}`
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        store.remove('ews_user')
        store.remove('ews_current_station')
        store.remove('ews_station_settings')
        store.remove('ews_container')
        cookies.remove('EWS_AUTH_KEY', { domain: `.${baseDomain}` })

        const prev = encodeURIComponent(location.href)
        window.location.href = `${loginUrl}?prev=${prev}&supports_okta_jwt=true`
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
